import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { Dropdown, MenuProps } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import {
  BiChevronLeft,
  BiChevronsLeft,
  BiDotsHorizontalRounded,
  BiEdit,
} from 'react-icons/bi';
import { twMerge } from 'tailwind-merge';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import RenderIf from '@/components/Common/RenderIf';
import { montserrat } from '@/components/Layout';

import {
  deleteSession,
  getSessionList,
  updateSessionTitle,
} from '@/services/AiChat';

export interface ChatItem {
  id: string;
  title: string;
  updatedAt: string;
}

interface SidebarChatsProps {
  activeChatId?: string;
  collapsed?: boolean;
  onSelectChat: (id: string) => void;
  onNewChat: () => void;
  setSidebarCollapsed: (
    collapsed: boolean | ((prev: boolean) => boolean),
  ) => void;
  setChats: React.Dispatch<React.SetStateAction<ChatItem[]>>;
  chats: ChatItem[];
}

const groupVariants = {
  hidden: {
    opacity: 0,
    y: -8,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: 'easeOut',
      when: 'beforeChildren',
      staggerChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

const SidebarChats: React.FC<SidebarChatsProps> = ({
  activeChatId,
  collapsed = false,
  onSelectChat,
  setSidebarCollapsed,
  onNewChat,
  setChats,
  chats,
}) => {
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [canShowText, setCanShowText] = useState(!collapsed);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const screens = useBreakpoint();
  const twScreens = useTailwindBreakpoint();

  useEffect(() => {
    if (editingId) inputRef.current?.focus();
  }, [editingId]);

  const getDateGroupLabel = (date: string) => {
    const d = moment(date);

    if (d.isSame(moment(), 'day')) return 'Today';
    if (d.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';

    return d.format('D MMM YYYY');
  };

  const fetchSessions = async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);

      const res = await getSessionList(page, 20);
      const list = res?.data ?? [];
      const more = res?.pagination?.has_next ?? false;

      const mapped: ChatItem[] = list.map((item: any) => ({
        id: item.chat_session_id,
        title: item.session_title || 'New Chat',
        updatedAt: getDateGroupLabel(item.updated_at),
      }));

      setChats((prev) => {
        const map = new Map<string, ChatItem>();

        [...prev, ...mapped].forEach((c) => {
          map.set(c.id, c);
        });

        return Array.from(map.values());
      });
      setHasMore(more);
      setPage((p) => p + 1);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const uniqueChats = React.useMemo(() => {
    const map = new Map<string, ChatItem>();
    chats.forEach((c) => map.set(c.id, c));
    return Array.from(map.values());
  }, [chats]);

  const groupedChats = uniqueChats.reduce<Record<string, ChatItem[]>>(
    (acc, chat) => {
      acc[chat.updatedAt] = acc[chat.updatedAt] || [];
      acc[chat.updatedAt]?.push(chat);
      return acc;
    },
    {},
  );

  useEffect(() => {
    if (collapsed) {
      setCanShowText(false); // hide immediately on collapse
      return;
    }

    const timer = setTimeout(() => {
      setCanShowText(true); // show AFTER expand animation
    }, 300);

    return () => clearTimeout(timer);
  }, [collapsed]);

  useEffect(() => {
    fetchSessions();
  }, []);

  const commitRename = async () => {
    if (!editingId || !draft.trim()) {
      setEditingId(null);
      return;
    }

    const newTitle = draft.trim();

    setChats((prev) =>
      prev.map((c) => (c.id === editingId ? { ...c, title: newTitle } : c)),
    );

    try {
      await updateSessionTitle(editingId, newTitle);
    } catch {
      setChats((prev) =>
        prev.map((c) => (c.id === editingId ? { ...c, title: 'New Chat' } : c)),
      );
    } finally {
      setEditingId(null);
    }
  };

  const handleDeleteSession = async (chat: ChatItem) => {
    const wasActive = chat.id === activeChatId;

    setChats((prev) => prev.filter((c) => c.id !== chat.id));

    if (wasActive) {
      onSelectChat('new');
    }

    try {
      await deleteSession(chat.id);
    } catch {
      setChats((prev) => [...prev, chat]);
      if (wasActive) {
        onSelectChat(chat.id);
      }
    }
  };

  const renderGroup = (label: string, items: ChatItem[], index: number) => {
    if (!items.length) return null;

    return (
      <AnimatePresence>
        {canShowText && (
          <motion.div
            key={label}
            variants={groupVariants}
            initial='hidden'
            animate='visible'
            className={index === 0 ? '' : 'mt-4'}
          >
            <div className='px-3 mb-3 text-xs leading-5 md:text-grayZl-300 text-grayZl-500'>
              {label}
            </div>

            <div className={twMerge('space-y-1', montserrat.className)}>
              {items.map((chat) => {
                const menuItems: MenuProps['items'] = [
                  {
                    key: 'rename',
                    icon: <PencilIcon className='h-4 w-4' />,
                    label: 'Rename',
                    onClick: () => {
                      setEditingId(chat.id);
                      setDraft(chat.title);
                    },
                  },
                  {
                    key: 'delete',
                    icon: <TrashIcon className='h-4 w-4 text-error-500' />,
                    label: <span className='text-error-500'>Delete</span>,
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      handleDeleteSession(chat);
                    },
                  },
                ];

                return (
                  <div
                    key={chat.id}
                    onClick={() => {
                      if (editingId) return;
                      onSelectChat(chat.id);
                    }}
                    className={twMerge(
                      clsx(
                        'group flex items-center justify-between md:px-2.5 px-4 md:py-2 py-[13px] rounded-md cursor-pointer gap-3  md:bg-transparent bg-stroke',
                      ),
                      activeChatId === chat.id
                        ? 'bg-grayZl-900'
                        : 'hover:bg-grayZl-900',
                    )}
                  >
                    {editingId === chat.id ? (
                      <input
                        ref={inputRef}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className='w-full bg-transparent text-sm outline-none'
                      />
                    ) : (
                      <span className='text-sm leading-[22px] line-clamp-2 font-medium'>
                        {chat.title}
                      </span>
                    )}

                    <Dropdown
                      trigger={['click']}
                      placement='bottomRight'
                      menu={{
                        items: menuItems,
                        onClick: (info) => info.domEvent.stopPropagation(),
                      }}
                      overlayStyle={{
                        fontFamily: montserrat.style.fontFamily,
                      }}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentElement!
                      }
                    >
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className='md:hidden  block md:group-hover:block  transition'
                      >
                        <BiDotsHorizontalRounded className='w-6 h-6 text-white' />
                      </button>
                    </Dropdown>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <aside
      className={clsx(
        'h-full md:bg-[#181818] bg-grayZl-950  flex flex-col transition-all duration-300',
        collapsed ? 'w-full md:w-14' : 'w-full md:w-[220px] lg:w-[260px]',
      )}
    >
      <div
        className={twMerge(
          ' hidden md:flex items-center',
          collapsed ? 'justify-center' : 'justify-end',
        )}
      >
        <button
          className='px-2.5 pt-2.5 pb-1'
          onClick={() => screens.md && setSidebarCollapsed((p) => !p)}
        >
          <BiChevronsLeft
            className={twMerge(
              'w-6 h-6',
              collapsed && 'rotate-180',
              'text-white transition',
            )}
          />
        </button>
      </div>
      <RenderIf condition={!twScreens.md}>
        <div className='p-2.5'>
          <BiChevronLeft
            onClick={(p) => setSidebarCollapsed(!p)}
            className='w-8 h-8 text-white'
          />
        </div>
      </RenderIf>
      <div className='px-2.5  flex items-center mt-2 md:mt-0 md:mb-0 mb-2'>
        <button
          onClick={onNewChat}
          className={clsx(
            'flex items-center rounded-md  w-full transition-all md:bg-transparent bg-stroke ',
            collapsed
              ? 'justify-center px-0 pt-2  gap-0 '
              : ' gap-2.5 md:px-2.5 md:py-2  px-4 py-[13px] ',
          )}
        >
          <BiEdit className='w-6 h-6 text-white' />
          <RenderIf condition={canShowText}>
            <p
              style={{
                fontFamily: montserrat.style.fontFamily,
              }}
              className='md:text-sm md:font-normal font-semibold  text-base leading-[22px] text-white'
            >
              New Chat
            </p>
          </RenderIf>
        </button>
      </div>

      <div
        ref={listRef}
        className='flex-1 overflow-y-auto px-2.5 pb-4 pt-[22px] zai-scrollbar'
        onScroll={(e) => {
          const el = e.currentTarget;
          if (
            el.scrollTop + el.clientHeight >= el.scrollHeight - 120 &&
            hasMore &&
            !loading
          ) {
            fetchSessions();
          }
        }}
      >
        {Object.entries(groupedChats).map(([label, items], index) => (
          <React.Fragment key={label}>
            {renderGroup(label, items, index)}
          </React.Fragment>
        ))}
        {loading && (
          <div className='px-3 py-2 text-xs text-gray-400'>Loading…</div>
        )}
      </div>
    </aside>
  );
};

export default SidebarChats;
